import { Close as CloseIcon } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useRef } from "react";
import ColorfulRole from "../../UIToolkit/ColorfulRole/ColorfulRole";
import {
	ThreadMessageHeader,
	ThreadMessageList,
	ThreadMessageListItem,
	ThreadMessageListSection,
	ThreadMessagesListDiv,
	ThreadMessagesRootDiv,
	ThreadMessageTypography,
	ThreadPreviewButton,
	ThreadPreviewListItem,
} from "./styled";

type AnonymousMessage = {
	user: string;
	text: string;
	// days offset from today (negative = past)
	daysAgo: number;
};

export const ANONYMOUS_MESSAGES: AnonymousMessage[] = [
	{ user: "Anonymous CNA", text: "The Hoyer lift in Unit B has been broken for weeks", daysAgo: -1 },
	{
		user: "Anonymous CNA",
		text: "Evening shift is always short. The assignments are too heavy and we can't give residents the care they deserve",
		daysAgo: -2,
	},
	{ user: "Anonymous CNA", text: "The call-out policy feels punitive. People are scared to stay home when they're actually sick", daysAgo: -3 },
	{ user: "Anonymous RN", text: "We're constantly running out of basic supplies mid-shift. It would help to have a reliable restock schedule", daysAgo: -4 },
	{ user: "Anonymous LPN", text: "Would really appreciate more feedback from management — not just when something goes wrong", daysAgo: -6 },
	{ user: "Anonymous CNA", text: "Break coverage is inconsistent. Some days we don't get a break at all on a 12-hour shift", daysAgo: -7 },
	{ user: "Anonymous LPN", text: "I always get the worst assignment", daysAgo: -7 },
	{ user: "Anonymous LPN", text: "The schedule changes last minute and it's hard to plan anything outside of work", daysAgo: -9 },
	{ user: "Anonymous CNA", text: "The charge nurse talks to staff in a disrespectful way and it makes the shift stressful", daysAgo: -11 },

	{ user: "Anonymous CNA", text: "Onboarding for new staff is really lacking. I was thrown into the unit with almost no orientation", daysAgo: -14 },
];

const lastMessage = ANONYMOUS_MESSAGES[0];

/** List preview item — looks like any other thread row */
export const AnonymousThreadPreview: FC<{ onClick: () => void }> = ({ onClick }) => {
	const lastMessageTime = DateTime.now().plus({ days: lastMessage.daysAgo });

	return (
		<ThreadPreviewListItem>
			<ThreadPreviewButton isFailedStatus={false} isEmptyMessage={false} onClick={onClick}>
				<span
					style={{
						justifySelf: "start",
						fontWeight: 600,
						gridRow: 1,
						gridColumn: 1,
					}}
				>
					Staff Feedback
				</span>
				<ThreadMessageTypography
					style={{
						opacity: "50%",
						justifySelf: "end",
						gridRow: 1,
						gridColumn: 2,
					}}
					title={lastMessageTime.toString()}
				>
					{lastMessageTime.toRelative()}
				</ThreadMessageTypography>
				<ThreadMessageTypography
					style={{
						maxWidth: 250,
						justifySelf: "start",
						gridRow: 2,
						gridColumn: 1,
					}}
					title={lastMessage.text}
				>
					{lastMessage.text}
				</ThreadMessageTypography>
			</ThreadPreviewButton>
		</ThreadPreviewListItem>
	);
};

/** Full read-only thread view */
export const AnonymousThreadView: FC<{ onClose: () => void }> = ({ onClose }) => {
	const headerRef = useRef<HTMLElement>(null);

	return (
		<ThreadMessagesRootDiv>
			<ThreadMessageHeader ref={headerRef}>
				<span>Staff Feedback</span>
				<span style={{ flexGrow: 1 }} />
				<IconButton onClick={onClose}>
					<CloseIcon />
				</IconButton>
			</ThreadMessageHeader>
			<ThreadMessageListSection
				style={{
					height: `calc(100% - ${headerRef?.current?.clientHeight ?? 0}px)`,
				}}
			>
				<ThreadMessagesListDiv>
					<Box sx={{ flex: 1, overflowY: "auto" }}>
						<ThreadMessageList>
							{ANONYMOUS_MESSAGES.map((msg) => {
								const sentFrom = msg.user === "Admin" ? "server" : "user";
								const messageTime = DateTime.now().plus({ days: msg.daysAgo });

								const role = msg.user.split(" ")[1]; // "Anonymous CNA" → "CNA"
								return (
									<ThreadMessageListItem key={`${msg.user}-${msg.daysAgo}`} sentFrom={sentFrom}>
										<Box sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 11, fontWeight: 600, opacity: 0.6 }}>
											<ColorfulRole role={role} />
											Anonymous
										</Box>
										<span style={{ fontWeight: 400, overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>{msg.text}</span>
										<Box sx={{ fontSize: 11, opacity: 0.5 }}>{messageTime.toRelative()}</Box>
									</ThreadMessageListItem>
								);
							})}
						</ThreadMessageList>
					</Box>
				</ThreadMessagesListDiv>
			</ThreadMessageListSection>
		</ThreadMessagesRootDiv>
	);
};
